import { getPlatformApiBaseUrl } from '../../api/client.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Table, TBody, Td, THead, Th, Tr } from '../../ui/index.ts';
import { ActionLink, DocsCallout, DocsSection, SnippetCard } from '../docs/primitives.tsx';
import type { AdminEndpoint, AdminField } from './reference.ts';
import { ADMIN_ENDPOINTS, ADMIN_ERROR_ENVELOPE, adminApiCurl } from './reference.ts';

/**
 * Bilingual reference for `GET/PATCH /v1/admin/*`.
 *
 * The base URL is the same platform origin the console uses for its own
 * API calls, so a staging bundle documents staging. Samples stay
 * language-neutral and use `$NXTPATH_ADMIN_KEY`.
 */

function adminApiBaseUrl(): string {
  const origin = getPlatformApiBaseUrl().replace(/\/$/, '');
  return `${origin}/v1/admin`;
}

export function ApiReference(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const base = adminApiBaseUrl();

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('docs.toast.copied'));
    } catch (err) {
      toast.error(localizeApiError(err, t));
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-api-reference">
      <DocsSection id="admin-api-overview" title={t('adminApi.doc.overview.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.overview.body')}
        </p>
        <DocsCallout tone="note">{t('adminApi.doc.overview.not_models')}</DocsCallout>
      </DocsSection>

      <DocsSection id="admin-api-base" title={t('adminApi.doc.base.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">{t('adminApi.doc.base.body')}</p>
        <SnippetCard
          title={t('adminApi.doc.base.snippet')}
          caption={null}
          code={base}
          copyLabel={t('adminApi.doc.base.copy')}
          onCopy={() => {
            void copy(base);
          }}
          testId="admin-api-base-url"
        />
      </DocsSection>

      <DocsSection id="admin-api-auth" title={t('adminApi.doc.auth.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">{t('adminApi.doc.auth.body')}</p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.auth.lifecycle')}
        </p>
        <SnippetCard
          title={t('adminApi.doc.auth.header')}
          caption={null}
          code={'Authorization: Bearer $NXTPATH_ADMIN_KEY'}
          copyLabel={t('adminApi.doc.base.copy')}
          onCopy={() => {
            void copy('Authorization: Bearer $NXTPATH_ADMIN_KEY');
          }}
          testId="admin-api-auth-header"
        />
        <h3 className="m-0 text-body font-medium text-ink">
          {t('adminApi.doc.auth.errors_title')}
        </h3>
        <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-body leading-relaxed text-ink-mid">
          <li>{t('adminApi.doc.auth.err.required')}</li>
          <li>{t('adminApi.doc.auth.err.invalid')}</li>
          <li>{t('adminApi.doc.auth.err.inactive')}</li>
          <li>{t('adminApi.doc.auth.err.rate')}</li>
        </ul>
      </DocsSection>

      <DocsSection id="admin-api-conventions" title={t('adminApi.doc.conventions.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.snake')}
        </p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.money')}
        </p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.time')}
        </p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.page')}
        </p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.window')}
        </p>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.conventions.error')}
        </p>
        <SnippetCard
          title={t('adminApi.doc.conventions.error_shape')}
          caption={null}
          code={ADMIN_ERROR_ENVELOPE}
          copyLabel={t('adminApi.doc.base.copy')}
          onCopy={() => {
            void copy(ADMIN_ERROR_ENVELOPE);
          }}
          testId="admin-api-error-envelope"
        />
      </DocsSection>

      <DocsSection id="admin-api-endpoints" title={t('adminApi.doc.endpoints.title')}>
        <div className="flex flex-col gap-4">
          {ADMIN_ENDPOINTS.map((endpoint) => (
            <EndpointCard
              key={endpoint.id}
              endpoint={endpoint}
              base={base}
              onCopy={(text) => {
                void copy(text);
              }}
            />
          ))}
        </div>
      </DocsSection>

      <DocsSection id="admin-api-openapi" title={t('adminApi.doc.openapi.title')}>
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {t('adminApi.doc.openapi.body')}
        </p>
        <ActionLink href={`${base}/openapi.json`} label={t('adminApi.doc.openapi.link')} external />
      </DocsSection>
    </div>
  );
}

function EndpointCard({
  endpoint,
  base,
  onCopy,
}: {
  readonly endpoint: AdminEndpoint;
  readonly base: string;
  readonly onCopy: (text: string) => void;
}): JSX.Element {
  const t = useT();
  const curl = adminApiCurl(base, endpoint);
  return (
    <article
      data-testid={`admin-api-endpoint-${endpoint.id}`}
      className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well p-4"
    >
      <h3 className="m-0 flex flex-wrap items-baseline gap-2 text-body font-medium text-ink">
        <span className="font-mono text-forest">{endpoint.method}</span>
        <span className="font-mono">{endpoint.path}</span>
      </h3>
      <p className="m-0 text-body leading-relaxed text-ink-mid">{t(endpoint.summaryKey)}</p>
      {endpoint.highlightKey !== undefined ? (
        <DocsCallout tone="warning">{t(endpoint.highlightKey)}</DocsCallout>
      ) : null}
      <FieldTable caption={t('adminApi.doc.params.query')} rows={endpoint.query} />
      <FieldTable caption={t('adminApi.doc.params.body')} rows={endpoint.body} />
      <FieldTable caption={t('adminApi.doc.params.response')} rows={endpoint.response} />
      {endpoint.noteKey !== undefined ? (
        <p className="m-0 text-body leading-relaxed text-ink-mid">{t(endpoint.noteKey)}</p>
      ) : null}
      <SnippetCard
        title={t('adminApi.doc.curl')}
        caption={null}
        code={curl}
        copyLabel={t('adminApi.doc.base.copy')}
        onCopy={() => onCopy(curl)}
        testId={`admin-api-curl-${endpoint.id}`}
      />
    </article>
  );
}

function FieldTable({
  caption,
  rows,
}: {
  readonly caption: string;
  readonly rows: readonly AdminField[];
}): JSX.Element {
  const t = useT();
  if (rows.length === 0) {
    return (
      <div>
        <h4 className="m-0 mb-1 text-label font-medium text-ink">{caption}</h4>
        <p className="m-0 text-body text-ink-low">{t('adminApi.doc.params.none')}</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="m-0 mb-2 text-label font-medium text-ink">{caption}</h4>
      <Table>
        <THead>
          <Tr>
            <Th>{t('adminApi.doc.params.name')}</Th>
            <Th>{t('adminApi.doc.params.detail')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr key={row.name}>
              <Td label={t('adminApi.doc.params.name')} mono heading>
                {row.name}
              </Td>
              <Td label={t('adminApi.doc.params.detail')} stack>
                {t(row.detailKey)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
